/**
 * IMPORTANT:
 * normally we don't want to put tab creating mutations or navigation calls in a useEffect
 * because it's too easy to have them run at unintended times
 * for deep linking we need to wait for initial getDashboard query data to run before creating tabs or navigating
 * so this is a special case where we're tightly controlling that each use effect only runs once
 * and only after the necessary data is loaded
 * it's a similar situation for a deep linked incident tab
 */
import { useGetDarkWebPost } from "api/apollo/queries/useGetDarkWebPost";
import useCreateDashboardTab from "api/mutations/useCreateDashboardTab";
import useGetDashboard from "api/queries/useGetDashboard";
import useGetIncident from "api/queries/useGetIncident";
import { Tab } from "autoGenSubTypes";
import { DashboardTabType } from "gql/apollo/graphql";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import useCurrentAlertsTab from "utils/useCurrentAlertsTab";
import useSnackbar from "utils/useSnackbar";
import useNavigateToExistingTab from "./useNavigateToExistingTab";

const useManageDeepLinkedTabs = () => {
	const { mutateAsync: createDashboardTab } = useCreateDashboardTab();

	const { pathname } = useLocation();
	const { handleError } = useSnackbar();
	const { navigateToExistingTab } = useNavigateToExistingTab();

	const { incidentId, tabKey, postId, columnsTabKey } = useParams();
	const { currentAlertsTab } = useCurrentAlertsTab(tabKey);

	/** START: logic to know when we have data and only run useEffects once */
	const [initialDashboardLoadSuccessful, setInitialDashboardLoadSuccessful] =
		useState<boolean>();
	const [initialIncidentLoadSuccessful, setInitialIncidentLoadSuccessful] =
		useState<boolean>();
	const [
		initialDarkWebPostLoadSuccessful,
		setInitialDarkWebPostLoadSuccessful,
	] = useState<boolean>();

	const {
		data: incident,
		status: statusIncident,
		isLoading: isLoadingIncident,
	} = useGetIncident({ _id: incidentId || "", follow_merges: true });

	const { data: dashboard, status: statusDashboard } = useGetDashboard();

	const {
		darkWebPost,
		loading: isLoadingDarkWebPost,
		error: isErrorDarkWebPost,
	} = useGetDarkWebPost(postId ?? "", !postId);
	const statusDarkWebPost =
		isErrorDarkWebPost ? "error"
		: isLoadingDarkWebPost ? "loading"
		: "success";

	useEffect(() => {
		if (
			initialDashboardLoadSuccessful === undefined &&
			statusDashboard === "success"
		) {
			setInitialDashboardLoadSuccessful(true);
		}
	}, [
		statusDashboard,
		initialDashboardLoadSuccessful,
		setInitialDashboardLoadSuccessful,
	]);

	useEffect(() => {
		if (initialIncidentLoadSuccessful === undefined) {
			if (statusIncident === "success") setInitialIncidentLoadSuccessful(true);
			else if (statusIncident === "error") {
				setInitialIncidentLoadSuccessful(false);
			}
		}
	}, [initialIncidentLoadSuccessful, statusIncident]);

	useEffect(() => {
		if (initialDarkWebPostLoadSuccessful === undefined) {
			if (statusDarkWebPost === "success") {
				setInitialDarkWebPostLoadSuccessful(true);
			} else if (statusDarkWebPost === "error") {
				setInitialDarkWebPostLoadSuccessful(false);
			}
		}
	}, [initialDarkWebPostLoadSuccessful, statusDarkWebPost]);
	/** END: logic to know when we have data and only run useEffects once */

	/** START: handling deep linked routes */
	/** deep linked to an incident tab */
	useEffect(() => {
		if (
			initialDashboardLoadSuccessful &&
			pathname.includes("/incident/") &&
			incidentId
		) {
			const incidentTabExists = dashboard?.tabs.reduce(
				(acc: boolean, cur: Tab) => {
					if (
						cur.__typename === "IncidentTab" &&
						cur.incident_tab_data.incident_id === incidentId
					) {
						return true;
					} else return acc;
				},
				false
			);

			if (initialIncidentLoadSuccessful && incident && !incidentTabExists) {
				/** incident exists, but we don't have a tab for it, so make one */
				createDashboardTab({
					params: {
						tab: {
							incident_tab_data: { incident_id: incidentId },
							type: DashboardTabType.INCIDENT,
						},
					},
				}).catch((error) => handleError(error));
			} else if (
				initialIncidentLoadSuccessful === false ||
				(!isLoadingIncident && !incident)
			) {
				/** incident doesn't exist so send user back to another tab, or to the home '/' route and show an error */
				navigateToExistingTab();
				handleError("This incident doesn't exist.");
			}
		}
		/**
		 * correctly ignored:
		 * we don't want this to run more than once
		 * but we need one thing in the array to make sure data is loaded
		 */
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialDashboardLoadSuccessful, initialIncidentLoadSuccessful]);

	/** deep linked to a dark web tab */
	useEffect(() => {
		if (
			initialDashboardLoadSuccessful &&
			pathname.includes("/darkweb/") &&
			postId
		) {
			const darkWebTabExists = dashboard?.tabs.reduce(
				(acc: boolean, cur: Tab) => {
					if (
						cur.__typename === "DarkWebTab" &&
						cur.dark_web_tab_data.dark_web_post_id === postId
					) {
						return true;
					} else return acc;
				},
				false
			);

			if (
				initialDarkWebPostLoadSuccessful &&
				darkWebPost &&
				!darkWebTabExists
			) {
				/** dark web post exists, but we don't have a tab for it, so make one */
				createDashboardTab({
					params: {
						tab: {
							type: DashboardTabType.DARK_WEB,
							dark_web_tab_data: { dark_web_post_id: postId },
						},
					},
				}).catch((error) => handleError(error));
			} else if (
				initialDarkWebPostLoadSuccessful === false ||
				(!isLoadingDarkWebPost && !darkWebPost)
			) {
				/** incident doesn't exist so send user back to another tab, or to the home '/' route and show an error */
				navigateToExistingTab();
				handleError("This dark web post doesn't exist.");
			}
		}
		/**
		 * correctly ignored:
		 * we don't want this to run more than once
		 * but we need one thing in the array to make sure data is loaded
		 */
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialDashboardLoadSuccessful, initialDarkWebPostLoadSuccessful]);

	/** deep linked to a columns tab */
	useEffect(() => {
		if (initialDashboardLoadSuccessful && columnsTabKey) {
			const currentColumnsTab = dashboard?.tabs.find(
				(tab) => tab.__typename === "ColumnsTab" && tab.key === columnsTabKey
			);

			if (!currentColumnsTab) {
				/** that tab doesn't exist, redirect to an existing tab */
				navigateToExistingTab();
				handleError("The tab that you tried to go to doesn't exist.");
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialDashboardLoadSuccessful]);

	/** deep linked to alerts tab */
	useEffect(() => {
		if (initialDashboardLoadSuccessful) {
			if (!currentAlertsTab && tabKey) {
				/** alerts tab doesn't exist so send user back to another tab, or to the home '/' route and show an error */
				navigateToExistingTab();
				handleError("The alerts tab that you tried to go to doesn't exist.");
			}
		}
		/**
		 * correctly ignored:
		 * we don't want this to run more than once
		 * but we need one thing in the array to make sure data is loaded
		 */
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialDashboardLoadSuccessful]);
	/** END: handling deep linked routes */
};

export default useManageDeepLinkedTabs;
